import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, MoveDown } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const HERO_IMG = "https://images.pexels.com/photos/16806670/pexels-photo-16806670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST_ITEMS = [
  { title: "Curated Brands", text: "Premium products from trusted names" },
  { title: "Corporate Pricing", text: "Designed for bulk orders" },
  { title: "Custom Curation", text: "Gifting tailored to your occasion" },
  { title: "Single Point of Contact", text: "One team, end to end" },
];

const PROCESS = [
  { n: "01", title: "Tell Us What You Need", text: "Share your quantity, budget, occasion and requirements." },
  { n: "02", title: "We Curate", text: "Our team creates a selection of suitable gifting options." },
  { n: "03", title: "Approve", text: "Review products, packaging and branding options." },
  { n: "04", title: "We Deliver", text: "We coordinate the order and fulfilment." },
];

const WHY = [
  { title: "Curated, Not Crowded", text: "We focus on products worth gifting rather than overwhelming you with endless choice." },
  { title: "Corporate First", text: "Every part of the experience is designed around bulk corporate requirements." },
  { title: "Trusted Brands", text: "Premium products from established, reliable names." },
  { title: "Flexible Curation", text: "Gift solutions built around budget, occasion and recipient." },
  { title: "End-to-End Support", text: "From product selection to fulfilment, work with one team." },
];

function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden border-y border-white/10 py-5 select-none">
      <div className="marquee-track flex whitespace-nowrap w-max">
        {row.map((item, i) => (
          <span key={i} className="flex items-center text-ivory/35 text-sm font-mono uppercase tracking-[0.3em]">
            <span className="px-8">{item}</span>
            <span className="w-1.5 h-1.5 bg-champagne/60 inline-block rotate-45" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [taxonomy, setTaxonomy] = useState({ brands: [], categories: [], collections: [] });
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    document.title = "THE GOOD GRID — Premium Corporate Gifting India";
    api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-charcoal text-ivory">
      {/* ============ HERO ============ */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
          <img src={HERO_IMG} alt="Premium glassware and corporate gift boxes in moody studio light" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-charcoal/70" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 px-4 sm:px-8 lg:px-16 pb-14 pt-40">
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} className="origin-left w-24 h-px bg-champagne mb-8" />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="text-[11px] font-mono uppercase tracking-[0.35em] text-champagne mb-6"
          >
            Premium Corporate Gifting
          </motion.p>
          <h1 data-testid="hero-headline" className="font-serif text-[13vw] sm:text-[9vw] lg:text-[7.2vw] leading-[0.98] tracking-tight">
            <MaskedLine delay={0.35}>Gifts That</MaskedLine>
            <MaskedLine delay={0.5}><span className="italic text-champagne-light">Mean</span> Business.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-ivory/70 text-base sm:text-lg leading-relaxed"
          >
            Premium corporate gifting, thoughtfully curated for people who matter to your business — glassware, cookware, cutlery, appliances and lifestyle gifts from trusted brands.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/request-quote" data-testid="hero-request-quote" className="group flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:bg-champagne-light transition-colors duration-300">
              Request a Quote <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/collections" data-testid="hero-explore-collections" className="flex items-center gap-2 border border-ivory/25 text-ivory text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:border-champagne hover:text-champagne transition-colors duration-300">
              Explore Collections
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute right-6 bottom-8 hidden lg:flex flex-col items-center gap-3 text-ivory/40"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scroll</span>
          <MoveDown size={14} className="animate-bounce" />
        </motion.div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section data-testid="trust-strip" className="border-b border-white/10">
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {TRUST_ITEMS.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08} className={`px-6 sm:px-8 py-8 lg:py-10 border-white/10 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i === 1 ? "lg:border-r" : ""} ${i === 2 ? "lg:border-r" : ""}`}>
              <p className="font-serif text-lg sm:text-xl text-ivory">{t.title}</p>
              <p className="text-xs text-ivory/45 mt-2">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Curated Products", "Considered Gifting", "Corporate Gifting, Without the Guesswork", "Premium Products for Professional Relationships"]} />

      {/* ============ CATEGORIES ============ */}
      <section data-testid="categories-section" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Eyebrow>The Catalogue</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">Shop by Category</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/collections" data-testid="categories-view-all" className="group flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors">
              View everything <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {taxonomy.categories.map((c, i) => (
            <Reveal key={c.name} delay={(i % 4) * 0.08} className={i % 4 === 1 || i % 4 === 2 ? "lg:mt-12" : ""}>
              <Link
                to={`/collections?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative block overflow-hidden aspect-[3/4] bg-charcoal-soft"
              >
                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover opacity-80 transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/90 via-[#121212]/10 to-transparent group-hover:from-[#121212]/95 transition-all duration-500" />
                <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6">
                  <p className="text-[10px] font-mono text-champagne tracking-[0.25em] mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif text-xl sm:text-2xl text-ivory">{c.name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ivory/0 group-hover:text-champagne mt-2 transition-colors duration-500">Explore Collection</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ BRANDS ============ */}
      <section id="brands" data-testid="brands-section" className="border-t border-white/10 px-4 sm:px-8 lg:px-16 py-20 lg:py-32 bg-[#0E0E0E]">
        <Reveal><Eyebrow>House of Brands</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-16 tracking-tight max-w-2xl">
            Trusted names, <span className="italic text-champagne-light">curated</span> for corporate India.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {taxonomy.brands.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.12}>
              <Link
                to={`/collections?brand=${encodeURIComponent(b.name)}`}
                data-testid={`brand-card-${b.name.toLowerCase()}`}
                className="group relative block overflow-hidden h-[420px] border border-white/10"
              >
                <img src={b.image} alt={`${b.name} corporate gifting products`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-[1.04] transition-all duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C] via-transparent to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-8">
                  <ArrowUpRight size={20} className="self-end text-ivory/30 group-hover:text-champagne group-hover:rotate-45 transition-all duration-500" />
                  <div>
                    <p className="font-serif text-4xl sm:text-5xl tracking-[0.08em] text-ivory group-hover:text-champagne-light transition-colors duration-500">{b.name}</p>
                    <p className="text-sm text-ivory/50 mt-3">{b.tagline}</p>
                    {b.subcategories?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {b.subcategories.map((s) => (
                          <span key={s} className="text-[10px] font-mono uppercase tracking-[0.15em] border border-white/15 text-ivory/60 px-2.5 py-1">{s}</span>
                        ))}
                      </div>
                    )}
                    {b.name === "VENUS" && (
                      <span className="inline-block mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-champagne border-b border-champagne/50 pb-0.5">
                        Gift Sets — Signature · Aura · Orion · Zen
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <p className="text-ivory/30 text-xs mt-8 max-w-lg">Brand names belong to their respective owners. Products are sourced and curated for corporate gifting.</p>
      </section>

      {/* ============ FEATURED PRODUCTS ============ */}
      <section data-testid="featured-section" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Eyebrow>Featured</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">Corporate Gifts in Focus</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/collections" data-testid="featured-view-all" className="group flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors">
              All products <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
          {featured.slice(0, 6).map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ COLLECTIONS / MANIFESTO ============ */}
      <section data-testid="collections-section" className="border-t border-white/10 bg-[#0E0E0E] px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
        <Reveal><Eyebrow>The Collections</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-20 tracking-tight max-w-3xl">
            Every occasion, <span className="italic text-champagne-light">considered.</span>
          </h2>
        </Reveal>
        <div className="space-y-20 lg:space-y-28">
          {taxonomy.collections.map((c, i) => (
            <Reveal key={c.slug}>
              <Link
                to={c.slug === "custom-corporate-gifting" ? "/custom-gifting" : `/collections?occasion=${encodeURIComponent(c.title)}`}
                data-testid={`collection-${c.slug}`}
                className={`group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "" : ""}`}
              >
                <div className={`lg:col-span-7 overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover opacity-85 group-hover:scale-[1.05] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                  </div>
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <p className="font-serif text-7xl lg:text-8xl text-outline leading-none mb-6">{c.number}</p>
                  <h3 className="font-serif text-3xl lg:text-4xl text-ivory group-hover:text-champagne-light transition-colors duration-500">{c.title}</h3>
                  <p className="text-ivory/50 mt-4 leading-relaxed max-w-md">{c.description}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-[11px] font-mono uppercase tracking-[0.22em] text-champagne">
                    Explore <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section data-testid="process-section" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
        <Reveal><Eyebrow>How It Works</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-16 tracking-tight">Corporate gifting, <span className="italic text-champagne-light">without the guesswork.</span></h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1} className="border-r border-b border-white/10 p-8 lg:p-10 group hover:bg-[#161616] transition-colors duration-500">
              <p className="font-mono text-champagne text-sm tracking-[0.2em] mb-8">{p.n}</p>
              <h3 className="font-serif text-2xl text-ivory mb-3">{p.title}</h3>
              <p className="text-sm text-ivory/45 leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <Link to="/request-quote" data-testid="process-start-order" className="group inline-flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:bg-champagne-light transition-colors duration-300">
            Start Your Corporate Order <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </section>

      {/* ============ WHY ============ */}
      <section data-testid="why-section" className="border-t border-white/10 bg-[#0E0E0E] px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Why THE GOOD GRID</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">
                From employee appreciation to executive gifting — gifts that <span className="italic text-champagne-light">leave an impression.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" data-testid="why-about-link" className="group inline-flex items-center gap-2 mt-10 text-[11px] font-mono uppercase tracking-[0.22em] text-ivory/60 hover:text-champagne transition-colors">
                About the studio <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 divide-y divide-white/10">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="py-7 flex gap-6 sm:gap-10 items-baseline group">
                  <span className="font-mono text-xs text-champagne tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl text-ivory group-hover:text-champagne-light transition-colors duration-300">{w.title}</h3>
                    <p className="text-sm text-ivory/45 mt-2 max-w-lg">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section data-testid="cta-banner" className="relative overflow-hidden border-t border-white/10 px-4 sm:px-8 lg:px-16 py-24 lg:py-36 text-center grain">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-[#181410] to-charcoal" />
        <div className="relative z-10">
          <Reveal><Eyebrow>Ready When You Are</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-6 tracking-tight">Let's build something<br /><span className="italic text-champagne-light">worth gifting.</span></h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link to="/request-quote" data-testid="cta-request-quote" className="bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-8 py-4 hover:bg-champagne-light transition-colors duration-300">
                Request a Quote
              </Link>
              <Link to="/contact" data-testid="cta-talk-to-team" className="border border-ivory/25 text-ivory text-[11px] font-mono uppercase tracking-[0.22em] px-8 py-4 hover:border-champagne hover:text-champagne transition-colors duration-300">
                Talk to Our Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
